import { bisector } from "d3-array";
import { axisBottom, axisLeft } from "d3-axis";
import { scaleLinear, scaleTime } from "d3-scale";
import { pointer, select } from "d3-selection";
import { curveMonotoneX, line } from "d3-shape";
import { format } from "date-fns";

import { LineChartData, LineChartDataPoint } from "./types";

import { clamp } from "../shared/clamp";
import { renderSideBars } from "../shared/render/sideBars";
import { Size } from "../shared/types";

const padding = {
  top: 10,
  right: 10,
  bottom: 30,
  left: 50,
};

export const lineChartUpdate = (
  ref: SVGSVGElement,
  size: Size,
  data: LineChartData,
) => {
  const svg = select(ref);

  const x = scaleTime()
    .domain(data.xDomain)
    .range([padding.left, size.width - padding.right]);

  const y = scaleLinear()
    .domain([0, Math.max(data.minValue, ...data.data.map((d) => d.value))])
    .range([size.height - padding.bottom, padding.top]);

  renderSideBars(svg, size, padding);

  const xAxisGroup = svg.select<SVGGElement>("g.xAxis");
  const yAxisGroup = svg.select<SVGGElement>("g.yAxis");

  xAxisGroup.attr("transform", `translate(0 ${size.height - padding.bottom})`);
  yAxisGroup.attr("transform", `translate(${padding.left} 0)`);

  const snapDates: Date[] = data.data.map((m) => new Date(m.datetime)) ?? [];

  const bisectSnap = bisector<Date, Date>((d) => d).left;

  function snapToNearestDate(d: Date) {
    if (snapDates.length === 0) return d;

    const i = bisectSnap(snapDates, d, 0);

    if (i === 0) return snapDates[0];

    const d0 = snapDates[i - 1];
    const d1 = snapDates[i];

    if (!d1) return d0;

    return d.getTime() - d0.getTime() > d1.getTime() - d.getTime() ? d1 : d0;
  }

  // --- TOOLTIP (HTML overlay) ---
  const host = ref.parentElement ?? document.body;
  const tooltip = select(host)
    .select<HTMLDivElement>("div.tooltip")
    .style("display", "none");

  // Focus line and dot
  const focusLine = svg
    .selectAll<SVGLineElement, unknown>("line.focus-line")
    .data([null])
    .join("line")
    .attr("class", "focus-line")
    .attr("y1", padding.top)
    .attr("y2", size.height - padding.bottom)
    .attr("stroke", "#999")
    .attr("stroke-width", 1)
    .attr("stroke-dasharray", "4,4")
    .style("display", "none");

  const focusDot = svg
    .selectAll<SVGCircleElement, unknown>("circle.focus-dot")
    .data([null])
    .join("circle")
    .attr("class", "focus-dot")
    .attr("r", 3.5)
    .attr("fill", "#00bcd4")
    .attr("stroke", "#fff")
    .attr("stroke-width", 1.5)
    .style("display", "none");

  const xAxis = axisBottom(x)
    .tickSize(8)
    .tickPadding(8)
    .ticks(6)
    .tickFormat((d) =>
      format(d instanceof Date ? d : new Date(d.valueOf()), "do MMM", {
        locale: data.locale,
      }),
    );

  const yAxis = axisLeft(y)
    .ticks(7)
    .tickSize(-size.width + padding.right + padding.left)
    .tickPadding(12)
    .tickFormat((value) => data.formatter(value.valueOf()));

  xAxisGroup.call(xAxis);
  yAxisGroup.call(yAxis);

  const lineGenerator = line<LineChartDataPoint>()
    .x((d) => x(d.datetime))
    .y((d) => y(d.value))
    .curve(curveMonotoneX);

  svg.select("path.line").datum(data.data).attr("d", lineGenerator);
  svg
    .select("line.average")
    .datum(data.data)
    .attr("opacity", data.average === null ? 0 : 1)
    .attr("x1", padding.left)
    .attr("x2", size.width - padding.right)
    .attr("y1", y(data.average ?? 0))
    .attr("y2", y(data.average ?? 0));

  svg
    .selectAll<SVGRectElement, unknown>("rect.overlay")
    .data([null])
    .join("rect")
    .attr("class", "overlay")
    .attr("x", padding.left)
    .attr("y", padding.top)
    .attr("width", size.width - padding.left - padding.right)
    .attr("height", size.height - padding.top - padding.bottom)
    .attr("fill", "transparent")
    .on("mouseenter", () => {
      focusLine.style("display", null);
      focusDot.style("display", null);
      tooltip.style("display", "block");
    })
    .on("mouseleave", () => {
      focusLine.style("display", "none");
      focusDot.style("display", "none");
      tooltip.style("display", "none");
    })
    .on("mousemove", onMouseMove);

  // Overlay for mouse events
  function onMouseMove(event: MouseEvent) {
    if (data.data.length === 0) return;

    const [mouseX] = pointer(event, ref);
    const hoveredDate = x.invert(mouseX);
    const snappedDate = snapToNearestDate(hoveredDate);

    let idx = bisectSnap(snapDates, snappedDate, 0);
    idx = clamp(idx, 0, data.data.length - 1);

    const d = data.data[idx];
    const snappedX = x(snapDates[idx]);

    focusLine.attr("x1", snappedX).attr("x2", snappedX);
    focusDot.attr("cx", snappedX).attr("cy", y(d.value));

    // Tooltip content
    const dateStr = format(d.datetime, "PPpp", { locale: data.locale });
    const valueStr = data.formatter(d.value);

    tooltip.html(
      `<div style='margin-bottom:4px'><strong>${dateStr}</strong></div><div>${valueStr}</div>`,
    );

    // Position tooltip
    const hostRect = host.getBoundingClientRect();
    const tooltipNode = tooltip.node();
    const tw = tooltipNode?.offsetWidth ?? 120;
    const th = tooltipNode?.offsetHeight ?? 40;

    const left = clamp(
      event.clientX - hostRect.left + 12,
      0,
      hostRect.width - tw,
    );
    const top = clamp(
      event.clientY - hostRect.top + 12,
      0,
      hostRect.height - th,
    );

    tooltip.style("left", `${left}px`).style("top", `${top}px`);
  }
};
